import { Injectable } from '@angular/core';
import { BaseStoreFacade } from '@city/data-access';
import { CustomBasePageMetaResolver, ResolversTextVariablesService } from '@core/feature';
import {
  BreadcrumbMeta,
  CanonicalPageResolver,
  OgDescriptionResolver,
  OgTitleResolver,
  PageBreadcrumbResolver,
  PageDescriptionResolver,
  PageHeadingResolver,
  PageImageResolver,
  PageLinkService,
  PageMetaResolver,
  PageRobotsMeta,
  PageRobotsResolver,
  PageTitleResolver,
  PageType,
  Product,
  ProductScope,
  ProductService,
  RoutingService,
  TranslationService,
} from '@spartacus/core';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';

/**
 * Резолвер метаданных для страницы деталей товара по типу страницы `PageType.PRODUCT_PAGE`.
 *
 * Получает SEO данные напрямую из объекта `product.seo` возвращаемого бэком.
 * Заголовок страницы, подзаголовок, описание, хлебные крошки, изображение,
 * теги robots и канонический URL берутся из данных товара.
 *
 * Резолвер независим от SmartEdit (CMS данные) и использует только данные товара.
 */
@Injectable({
  providedIn: 'root',
})
export class ProductPageMetaResolver
  extends PageMetaResolver
  implements
    PageTitleResolver,
    PageHeadingResolver,
    PageBreadcrumbResolver,
    PageImageResolver,
    PageDescriptionResolver,
    PageRobotsResolver,
    CanonicalPageResolver,
    OgTitleResolver,
    OgDescriptionResolver
{
  private product$: Observable<Product> = this.routingService.getRouterState().pipe(
    map((state) => state.state.params['productCode']),
    filter((code) => !!code),
    switchMap((code) => this.productService.get(code, ProductScope.DETAILS)),
    filter((p) => Boolean(p)),
  );

  constructor(
    private routingService: RoutingService,
    private productService: ProductService,
    private translation: TranslationService,
    private basePageMetaResolver: CustomBasePageMetaResolver,
    private pageLinkService: PageLinkService,
    private resolversTextVariablesService: ResolversTextVariablesService,
    private baseStoreFacade: BaseStoreFacade,
  ) {
    super();
    this.pageType = PageType.PRODUCT_PAGE;
  }

  /**
   * Получает подзаголовок страницы товара (обычно используется для тега H1).
   * В качестве подзаголовка используется название товара.
   */
  resolveHeading(): Observable<string> {
    return this.product$.pipe(
      switchMap((p: Product) =>
        this.translation.translate('pageMetaResolver.product.heading', {
          heading: p.name,
        }),
      ),
    );
  }

  /**
   * Получает заголовок страницы товара. Берётся из `product.seo.title`.
   */
  resolveTitle(): Observable<string> {
    return this.product$.pipe(
      switchMap((product) => {
        if (product.seo?.title) {
          return this.resolversTextVariablesService.resolveTemplateString(product.seo.title);
        }

        return of('');
      }),
    );
  }

  /**
   * Получает заголовок OpenGraph из `product.seo.ogTitle`.
   */
  resolveOgTitle(): Observable<string> {
    return this.product$.pipe(
      switchMap((product) => {
        if (product.seo?.ogTitle) {
          return this.resolversTextVariablesService.resolveTemplateString(product.seo.ogTitle);
        }

        return of('');
      }),
    );
  }

  /**
   * Получает хлебные крошки для навигации по странице товара.
   * Включает главную страницу, категории товара и название товара.
   */
  resolveBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({
      breadcrumbs: this.getBreadcrumbs(),
      product: this.product$ as Observable<Product>,
    }).pipe(map(({ breadcrumbs, product }) => [...breadcrumbs.flat(), { label: product.name } as BreadcrumbMeta]));
  }

  /**
   * Получает тип страницы для разметки OpenGraph.
   * Возвращает значение "product" для товарных страниц.
   */
  resolveType(): Observable<string> {
    return of('product');
  }

  /**
   * Получает описание страницы из `product.seo.description`.
   */
  resolveDescription(): Observable<string> {
    return this.product$.pipe(
      switchMap((product) => {
        if (product.seo?.description) {
          return this.resolversTextVariablesService.resolveTemplateString(product.seo.description);
        }

        return of('');
      }),
    );
  }

  /**
   * Получает описание OpenGraph из `product.seo.ogDescription`.
   */
  resolveOgDescription(): Observable<string> {
    return this.product$.pipe(
      switchMap((product) => {
        if (product.seo?.ogDescription) {
          return this.resolversTextVariablesService.resolveTemplateString(product.seo.ogDescription);
        }

        return of('');
      }),
    );
  }

  /**
   * Получает директивы мета-тега robots.
   * Используется поисковыми системами для определения индексации страницы
   * и следования по ссылкам.
   */
  resolveRobots(): Observable<PageRobotsMeta[]> {
    return this.basePageMetaResolver.resolveRobots();
  }

  /**
   * Основное изображение страницы товара.
   * Возвращает zoom-версию первичного изображения товара.
   */
  resolveImage(): Observable<string> {
    return this.product$.pipe(map((product) => (<any>product.images?.PRIMARY)?.zoom?.url ?? null));
  }

  /**
   * URL для страницы товара.
   *
   * Если URL содержит город, то canonical тоже должен содержать город
   *
   * Городская часть урла вырезается из сгенерированного URL только в том случае,
   * если его не было в фактически запрошенном URL текущего роута.
   */
  resolveCanonicalUrl(): Observable<string> {
    return this.product$.pipe(
      switchMap((product) => this.findBaseProduct(product)),
      switchMap((product) =>
        combineLatest({
          baseStoreList: this.baseStoreFacade.getAll(),
          routerState: this.routingService.getRouterState(),
        }).pipe(
          map(({ baseStoreList, routerState }) => {
            const url = this.routingService.getFullUrl({
              cxRoute: 'product',
              params: product,
            });

            const parsed = new URL(url);
            const segments = parsed.pathname.split('/').filter(Boolean);
            const cityCodes = baseStoreList.map((store) => store.code);

            const currentSegments = (routerState.state.url || '').split('/').filter(Boolean);
            const currentUrlHasCity = cityCodes.includes(currentSegments[0]);

            // Убираем город из canonical только если его не было в запрошенном URL
            if (!currentUrlHasCity && cityCodes.includes(segments[0])) {
              parsed.pathname = '/' + segments.slice(1).join('/');
            }

            return this.pageLinkService.getCanonicalUrl({}, parsed.toString());
          }),
        ),
      ),
    );
  }

  /**
   * Формирует хлебные крошки из категорий товара.
   * Включает ссылку на главную страницу и все категории товара.
   */
  private getBreadcrumbs(): Observable<BreadcrumbMeta[]> {
    return combineLatest({ product: this.product$.pipe(), label: this.translation.translate('common.home') }).pipe(
      map(({ product, label }) => {
        const breadcrumbs = [];
        breadcrumbs.push({ label, link: '/' });
        for (const { name, code, url } of product.categories || []) {
          breadcrumbs.push({
            label: name || code,
            link: url,
          } as BreadcrumbMeta);
        }

        return breadcrumbs;
      }),
    );
  }

  /**
   * Находит базовый товар
   */
  private findBaseProduct(product: Product): Observable<Product> {
    if (product?.baseProduct) {
      return this.productService.get(product.baseProduct, ProductScope.LIST).pipe(
        filter((p) => Boolean(p)),
        switchMap((baseProduct) => this.findBaseProduct(baseProduct)),
      );
    }

    return of(product);
  }
}
